import React from "react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Collapse, IconButton, List, ListItemIcon, ListItemText } from "@mui/material";
import Logo from "@components/Logo/Logo";
import {
  DrawerHeader,
  DrawerStyled,
  NavItemContainerStyled,
  NavItemDividerStyled,
  SidebarListItemButtonNestedStyled,
  SidebarListItemButtonStyled,
} from "@components/MainNavigation/Sidebar/Sidebar.styles";
import type { NavItemProps, SidebarProps } from "@components/MainNavigation/Sidebar/Sidebar.types";
import { useNavigationGuard } from "@context/NavigationGuardContext";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { AppNavigationItems } from "@utils/navigation";
import { useNavigate } from "react-router-dom";

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, onOpen }) => {
  const navigate = useNavigate();
  const { requestNavigation } = useNavigationGuard();
  const { userAccessPermissions, customSecurityClearancePath } = useAuthStore();

  const navItems = AppNavigationItems(
    userAccessPermissions,
    window.location.pathname,
    customSecurityClearancePath,
  );

  const NavItem: React.FC<NavItemProps> = ({ title, path, icon, submenu, isVisible }) => {
    const [open, setOpen] = React.useState(true);

    const handleClick = () => {
      setOpen(!open);
    };

    if (!isVisible) return null; // Skip rendering if not visible

    return submenu ? (
      <NavItemContainerStyled>
        <NavItemDividerStyled>
          <SidebarListItemButtonStyled
            onClick={handleClick}
            data-testid={`sidebar-nav-item-${title}`}
          >
            <ListItemText primary={title} />
          </SidebarListItemButtonStyled>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {submenu.map((submenuItem) => (
                <SidebarListItemButtonNestedStyled
                  key={submenuItem.title}
                  onClick={() => requestNavigation(() => navigate(submenuItem.path))}
                >
                  <ListItemText primary={submenuItem.title} />
                </SidebarListItemButtonNestedStyled>
              ))}
            </List>
          </Collapse>
        </NavItemDividerStyled>
      </NavItemContainerStyled>
    ) : (
      <NavItemContainerStyled>
        <SidebarListItemButtonStyled
          onClick={() => requestNavigation(() => navigate(path))}
          data-testid={`sidebar-nav-item-${title}`}
        >
          {icon && <ListItemIcon>{icon}</ListItemIcon>}
          <ListItemText primary={title} />
        </SidebarListItemButtonStyled>
        <NavItemDividerStyled />
      </NavItemContainerStyled>
    );
  };

  return (
    <DrawerStyled anchor={"left"} open={open} onClose={onClose} onOpen={onOpen}>
      <DrawerHeader>
        <Logo height={40} />
        <IconButton
          onClick={onClose}
          data-testid="sidebar-close-button"
          color="secondary"
          sx={{ ml: 1 }}
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DrawerHeader>

      {/* Main nav Items */}
      <List component="nav" disablePadding>
        {navItems.map((item) => (
          <NavItem key={item.title} {...item} />
        ))}
      </List>
    </DrawerStyled>
  );
};

export default Sidebar;
